import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Box, Chip, CircularProgress, Avatar, Typography } from '@mui/material';
import { Person } from '@mui/icons-material';
import { useDataProvider } from 'react-admin';
import { useEmployeeSearch, type EmployeeSearchResult } from './useEmployeeSearch';

interface EmployeeSearchAutocompleteProps {
  label?: string;
  placeholder?: string;
  onEmployeeSelect?: (employee: EmployeeSearchResult) => void;
  onClear?: () => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
  helperText?: string;
  error?: boolean;
}

interface Option {
  id: string;
  employeeNumber: string;
  demographics: {
    firstName?: string;
    lastName?: string;
    race: string;
    gender: string;
  };
  fullName: string;
  organisationalHierarchy: {
    department: string;
  };
  seniority: {
    occupationalLevel: string;
    jobTitle: string;
  };
  contractType: string;
  status: string;
}

export function EmployeeSearchAutocomplete({
  label = 'Select Employee',
  placeholder = 'Search by name, employee number, or ID...',
  onEmployeeSelect,
  onClear,
  size = 'medium',
  disabled = false,
  helperText,
  error = false,
}: EmployeeSearchAutocompleteProps) {
  const [inputValue, setInputValue] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeSearchResult | null>(null);
  const dataProvider = useDataProvider();
  const { results, isLoading, search, clearResults } = useEmployeeSearch({ dataProvider });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      search(inputValue);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [inputValue, search]);

  const handleInputChange = (_event: React.ChangeEvent<unknown>, value: string) => {
    setInputValue(value);
  };

  const handleChange = (_event: React.ChangeEvent<unknown>, value: Option | null) => {
    if (value) {
      setSelectedEmployee(value);
      onEmployeeSelect?.(value);
    } else {
      setSelectedEmployee(null);
      clearResults();
      onClear?.();
    }
  };

  const handleClear = () => {
    setSelectedEmployee(null);
    setInputValue('');
    clearResults();
    onClear?.();
  };

  const getOptionLabel = (option: Option): string => {
    return option.fullName;
  };

  const isOptionEqualToValue = (option: Option, value: Option): boolean => {
    return option.id === value.id;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete<Option, false, false, false>
        options={results}
        loading={isLoading}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        value={selectedEmployee}
        onChange={handleChange}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        disabled={disabled}
        freeSolo={false}
        size={size}
        noOptionsText={results.length === 0 && inputValue.length >= 2 ? 'No employees found' : 'Type to search'}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                    <Person color="action" />
                  </Box>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          return (
            <Box
              component="li"
              key={key}
              {...rest}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1.5,
                px: 2,
              }}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: 'primary.main',
                  fontSize: 14,
                }}
              >
                {(option.demographics?.firstName?.charAt(0) ?? '')}{(option.demographics?.lastName?.charAt(0) ?? '')}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {option.fullName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.employeeNumber} • {option.organisationalHierarchy?.department}
                </Typography>
              </Box>
            </Box>
          );
        }}
      />
      {selectedEmployee && (
        <Box sx={{ mt: 1.5 }}>
          <Chip
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selectedEmployee.fullName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({selectedEmployee.employeeNumber})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  • {selectedEmployee.organisationalHierarchy?.department}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  • {selectedEmployee.seniority?.jobTitle}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  • {selectedEmployee.contractType}
                </Typography>
              </Box>
            }
            onDelete={handleClear}
            deleteIcon={
              <Box sx={{ display: 'flex', alignItems: 'center', fontSize: 16 }}>
                ×
              </Box>
            }
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'success.light',
              color: 'success.dark',
              '& .MuiChip-deleteIcon': {
                color: 'success.dark',
                '&:hover': {
                  color: 'success.dark',
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}